import { useCallback, useEffect, useState } from "react";
import {
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Download,
  Plus,
  Send,
} from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Loading, SocietyPageHeader } from "../../components/shared";

const EMPTY_MEETING = {
  meeting_type: "AGM",
  title: "",
  scheduled_at: "",
  location: "",
  meeting_link: "",
  description: "",
  audience: {
    mode: "ALL_RESIDENTS",
    building_ids: [],
    wing_ids: [],
    unit_ids: [],
  },
  supporting_doc_ids: [],
};

function AudienceFields({ bootstrap, value, onChange }) {
  const audience = value.audience;
  const update = (key, values) => {
    onChange({ ...value, audience: { ...audience, [key]: values } });
  };
  const selectMode = (mode) => {
    onChange({
      ...value,
      audience: { mode, building_ids: [], wing_ids: [], unit_ids: [] },
    });
  };

  return (
    <div
      className="grid gap-3 sm:grid-cols-2"
      data-testid="meeting-audience-fields"
    >
      <label className="text-sm">
        <span className="mb-1 block font-medium">Audience</span>
        <select
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2"
          data-testid="meeting-audience-mode"
          onChange={(event) => selectMode(event.target.value)}
          value={audience.mode}
        >
          <option value="ALL_RESIDENTS">All residents</option>
          <option value="OWNERS">Owners</option>
          <option value="TENANTS">Tenants</option>
          <option value="SELECTED_BUILDING_WING">
            Selected building / wing
          </option>
          <option value="SELECTED_UNITS">Selected units</option>
          <option value="COMMITTEE_ONLY">Committee only (private)</option>
        </select>
      </label>
      {audience.mode === "SELECTED_UNITS" && (
        <label className="text-sm">
          <span className="mb-1 block font-medium">Units</span>
          <select
            className="min-h-24 w-full rounded-md border border-slate-300 bg-white px-3 py-2"
            data-testid="meeting-audience-units"
            multiple
            onChange={(event) =>
              update(
                "unit_ids",
                [...event.target.selectedOptions].map((item) => item.value),
              )
            }
            value={audience.unit_ids}
          >
            {bootstrap.units.map((unit) => (
              <option key={unit.unit_id} value={unit.unit_id}>
                {unit.unit_number}
              </option>
            ))}
          </select>
        </label>
      )}
      {audience.mode === "SELECTED_BUILDING_WING" && (
        <>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Buildings</span>
            <select
              className="min-h-24 w-full rounded-md border border-slate-300 bg-white px-3 py-2"
              data-testid="meeting-audience-buildings"
              multiple
              onChange={(event) =>
                update(
                  "building_ids",
                  [...event.target.selectedOptions].map((item) => item.value),
                )
              }
              value={audience.building_ids}
            >
              {bootstrap.buildings.map((building) => (
                <option key={building.building_id} value={building.building_id}>
                  {building.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Wings</span>
            <select
              className="min-h-24 w-full rounded-md border border-slate-300 bg-white px-3 py-2"
              data-testid="meeting-audience-wings"
              multiple
              onChange={(event) =>
                update(
                  "wing_ids",
                  [...event.target.selectedOptions].map((item) => item.value),
                )
              }
              value={audience.wing_ids}
            >
              {bootstrap.wings.map((wing) => (
                <option key={wing.wing_id} value={wing.wing_id}>
                  {wing.name}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
    </div>
  );
}

export default function SocietyMeetings() {
  const [bootstrap, setBootstrap] = useState(null);
  const [meeting, setMeeting] = useState(EMPTY_MEETING);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [agenda, setAgenda] = useState({
    title: "",
    notes: "",
    decision_required: false,
  });
  const [attendance, setAttendance] = useState({
    person_id: "",
    status: "PRESENT",
  });
  const [quorum, setQuorum] = useState({
    eligible_count: "",
    required_count: "",
    notes: "",
  });
  const [schedule, setSchedule] = useState({ scheduled_at: "", reason: "" });
  const [minutes, setMinutes] = useState({ body: "", action_items: "" });
  const [resolution, setResolution] = useState({
    title: "",
    text: "",
    outcome: "",
    recorded_for: "",
    recorded_against: "",
    recorded_abstain: "",
  });

  const load = useCallback(async () => {
    try {
      const response = await api.get("/society-meetings/bootstrap");
      setBootstrap(response.data);
      if (selected) {
        const updated = response.data.meetings.find(
          (item) => item.meeting_id === selected.meeting_id,
        );
        setSelected(updated || null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load meetings");
      setBootstrap({
        meetings: [],
        documents: [],
        units: [],
        buildings: [],
        wings: [],
        people: [],
      });
    }
  }, [selected]);

  const loadDetail = async (item) => {
    setSelected(item);
    try {
      const response = await api.get(
        `/society-meetings/meetings/${item.meeting_id}`,
      );
      setDetail(response.data);
      setMinutes({
        body: response.data.meeting.minutes?.body || "",
        action_items: (response.data.meeting.minutes?.action_items || []).join(
          "\n",
        ),
      });
      setQuorum({
        eligible_count: response.data.meeting.quorum?.eligible_count ?? "",
        required_count: response.data.meeting.quorum?.required_count ?? "",
        notes: response.data.meeting.quorum?.notes || "",
      });
      setSchedule({
        scheduled_at: (response.data.meeting.scheduled_at || "").slice(0, 16),
        reason: "",
      });
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not load meeting record",
      );
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const run = async (request, message) => {
    try {
      const response = await request();
      toast.success(message);
      await load();
      if (response.data?.meeting_id) await loadDetail(response.data);
      if (selected) await loadDetail(selected);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update meeting");
      return null;
    }
  };

  const createMeeting = async () => {
    const created = await run(
      () => api.post("/society-meetings/meetings", meeting),
      "Meeting draft saved",
    );
    if (created) {
      setMeeting(EMPTY_MEETING);
      await loadDetail(created);
    }
  };

  const downloadMeetingPdf = async (documentType) => {
    if (!selectedMeeting?.meeting_id) return;
    try {
      const response = await api.get(
        `/pdf/society-meetings/${selectedMeeting.meeting_id}/${documentType}/pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create the meeting PDF");
    }
  };

  const selectedMeeting = detail?.meeting || selected;
  const canManageSchedule = ["DRAFT", "PUBLISHED"].includes(
    selectedMeeting?.status,
  );
  const canRecordMeeting = ["PUBLISHED", "COMPLETED"].includes(
    selectedMeeting?.status,
  );

  if (!bootstrap) return <Loading />;

  return (
    <div data-testid="society-meetings-page">
      <SocietyPageHeader
        icon={CalendarDays}
        tone="blue"
        subtitle="Plan, publish, record, and preserve Society meeting decisions."
        title="Meetings & AGM"
      />
      <section
        className="border border-slate-200 bg-white p-5"
        data-testid="meeting-create-panel"
      >
        <div className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-blue-700" />
          <h2 className="font-display text-xl font-semibold">Create meeting</h2>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block font-medium">Meeting type</span>
            <select
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2"
              data-testid="meeting-type"
              onChange={(event) =>
                setMeeting({ ...meeting, meeting_type: event.target.value })
              }
              value={meeting.meeting_type}
            >
              <option value="AGM">AGM</option>
              <option value="SGM">SGM</option>
              <option value="COMMITTEE_MEETING">Committee Meeting</option>
              <option value="GENERAL_MEETING">General Meeting</option>
              <option value="OTHER">Other</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">
              Scheduled date and time
            </span>
            <Input
              data-testid="meeting-scheduled-at"
              onChange={(event) =>
                setMeeting({ ...meeting, scheduled_at: event.target.value })
              }
              type="datetime-local"
              value={meeting.scheduled_at}
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block font-medium">Meeting title</span>
            <Input
              data-testid="meeting-title"
              onChange={(event) =>
                setMeeting({ ...meeting, title: event.target.value })
              }
              value={meeting.title}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Location</span>
            <Input
              data-testid="meeting-location"
              onChange={(event) =>
                setMeeting({ ...meeting, location: event.target.value })
              }
              value={meeting.location}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Online meeting link</span>
            <Input
              data-testid="meeting-link"
              onChange={(event) =>
                setMeeting({ ...meeting, meeting_link: event.target.value })
              }
              value={meeting.meeting_link}
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block font-medium">Context</span>
            <Textarea
              data-testid="meeting-description"
              onChange={(event) =>
                setMeeting({ ...meeting, description: event.target.value })
              }
              rows={3}
              value={meeting.description}
            />
          </label>
        </div>
        <div className="mt-5">
          <AudienceFields
            bootstrap={bootstrap}
            onChange={setMeeting}
            value={meeting}
          />
        </div>
        <fieldset className="mt-5 border-t border-slate-200 pt-4">
          <legend className="text-sm font-medium">
            Supporting documents from the Society vault
          </legend>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {bootstrap.documents.map((document) => (
              <label
                className="flex items-center gap-2 text-sm"
                key={document.doc_id}
              >
                <input
                  checked={meeting.supporting_doc_ids.includes(document.doc_id)}
                  data-testid={`meeting-document-${document.doc_id}`}
                  onChange={(event) =>
                    setMeeting({
                      ...meeting,
                      supporting_doc_ids: event.target.checked
                        ? [...meeting.supporting_doc_ids, document.doc_id]
                        : meeting.supporting_doc_ids.filter(
                            (id) => id !== document.doc_id,
                          ),
                    })
                  }
                  type="checkbox"
                />
                {document.title}
              </label>
            ))}
          </div>
        </fieldset>
        <Button
          className="mt-5"
          data-testid="meeting-save-draft"
          onClick={createMeeting}
        >
          <Plus className="h-4 w-4" />
          Save draft
        </Button>
      </section>

      <section className="mt-8" data-testid="meeting-register">
        <h2 className="font-display text-2xl font-semibold">
          Meeting register
        </h2>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {bootstrap.meetings.length ? (
            bootstrap.meetings.map((item) => (
              <button
                className={
                  "border border-slate-200 bg-white p-4 text-left " +
                  "transition-colors hover:border-blue-500 hover:bg-blue-50"
                }
                data-testid={`meeting-record-${item.meeting_id}`}
                key={item.meeting_id}
                onClick={() => loadDetail(item)}
              >
                <p className="text-xs font-semibold text-blue-700">
                  {item.meeting_number} ·{" "}
                  {item.meeting_type.replaceAll("_", " ")}
                </p>
                <h3 className="mt-1 font-display text-lg font-semibold">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  {item.status} ·{" "}
                  {(item.scheduled_at || "").replace("T", " ").slice(0, 16)}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  RSVP: {item.rsvp_counts?.YES || 0} yes · {item.minutes_state}{" "}
                  minutes
                </p>
              </button>
            ))
          ) : (
            <p
              className="border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600"
              data-testid="meeting-empty"
            >
              No Society meetings have been created.
            </p>
          )}
        </div>
      </section>

      {selectedMeeting && (
        <section
          className="mt-8 border-t border-slate-200 pt-8"
          data-testid="meeting-detail-workspace"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold text-blue-700">
                {selectedMeeting.status} · {selectedMeeting.minutes_state}{" "}
                MINUTES
              </p>
              <h2 className="mt-1 font-display text-2xl font-semibold">
                {selectedMeeting.title}
              </h2>
            </div>
            {selectedMeeting.status === "DRAFT" && (
              <Button
                data-testid="meeting-publish"
                onClick={() =>
                  run(
                    () =>
                      api.post(
                        `/society-meetings/meetings/${selectedMeeting.meeting_id}/publish`,
                      ),
                    "Meeting published",
                  )
                }
              >
                <Send className="h-4 w-4" />
                Publish meeting
              </Button>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="meeting-letterhead-actions">
            <Button
              data-testid="meeting-notice-letterhead-pdf"
              onClick={() => downloadMeetingPdf("notice")}
              size="sm"
              variant="outline"
            >
              <Download className="h-4 w-4" />
              Notice preview PDF
            </Button>
            <Button
              data-testid="meeting-agenda-letterhead-pdf"
              onClick={() => downloadMeetingPdf("agenda")}
              size="sm"
              variant="outline"
            >
              <Download className="h-4 w-4" />
              Agenda preview PDF
            </Button>
            <Button
              data-testid="meeting-proceedings-letterhead-pdf"
              onClick={() => downloadMeetingPdf("proceedings")}
              size="sm"
              variant="outline"
            >
              <Download className="h-4 w-4" />
              Proceedings PDF
            </Button>
          </div>
          {selectedMeeting.status === "DRAFT" && (
            <section
              className="mt-6 border border-slate-200 bg-white p-5"
              data-testid="meeting-agenda-panel"
            >
              <h3 className="font-display text-lg font-semibold">Agenda</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Input
                  data-testid="meeting-agenda-title"
                  onChange={(event) =>
                    setAgenda({ ...agenda, title: event.target.value })
                  }
                  placeholder="Agenda item"
                  value={agenda.title}
                />
                <Input
                  data-testid="meeting-agenda-notes"
                  onChange={(event) =>
                    setAgenda({ ...agenda, notes: event.target.value })
                  }
                  placeholder="Supporting note"
                  value={agenda.notes}
                />
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  checked={agenda.decision_required}
                  data-testid="meeting-agenda-decision-required"
                  onChange={(event) =>
                    setAgenda({
                      ...agenda,
                      decision_required: event.target.checked,
                    })
                  }
                  type="checkbox"
                />
                Decision required
              </label>
              <Button
                className="mt-4"
                data-testid="meeting-add-agenda"
                onClick={async () => {
                  const result = await run(
                    () =>
                      api.post(
                        `/society-meetings/meetings/${selectedMeeting.meeting_id}/agenda`,
                        agenda,
                      ),
                    "Agenda item added",
                  );
                  if (result)
                    setAgenda({
                      title: "",
                      notes: "",
                      decision_required: false,
                    });
                }}
                size="sm"
                variant="outline"
              >
                <Plus className="h-4 w-4" />
                Add agenda
              </Button>
            </section>
          )}
          {canManageSchedule && (
            <section
              className="mt-6 border border-slate-200 bg-white p-5"
              data-testid="meeting-schedule-panel"
            >
              <h3 className="font-display text-lg font-semibold">
                Schedule control
              </h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Input
                  data-testid="meeting-reschedule-at"
                  onChange={(event) =>
                    setSchedule({
                      ...schedule,
                      scheduled_at: event.target.value,
                    })
                  }
                  type="datetime-local"
                  value={schedule.scheduled_at}
                />
                <Input
                  data-testid="meeting-schedule-reason"
                  onChange={(event) =>
                    setSchedule({ ...schedule, reason: event.target.value })
                  }
                  placeholder="Reason for reschedule or cancellation"
                  value={schedule.reason}
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  data-testid="meeting-reschedule"
                  onClick={() =>
                    run(
                      () =>
                        api.post(
                          `/society-meetings/meetings/${selectedMeeting.meeting_id}/reschedule`,
                          schedule,
                        ),
                      "Meeting rescheduled with history preserved",
                    )
                  }
                  size="sm"
                  variant="outline"
                >
                  Reschedule
                </Button>
                <Button
                  data-testid="meeting-cancel"
                  onClick={() =>
                    run(
                      () =>
                        api.post(
                          `/society-meetings/meetings/${selectedMeeting.meeting_id}/cancel`,
                          { reason: schedule.reason },
                        ),
                      "Meeting cancelled; its record is preserved",
                    )
                  }
                  size="sm"
                  variant="outline"
                >
                  Cancel meeting
                </Button>
              </div>
              {selectedMeeting.schedule_history?.length > 0 && (
                <p
                  className="mt-3 text-xs text-slate-600"
                  data-testid="meeting-schedule-history"
                >
                  {selectedMeeting.schedule_history.length} recorded schedule
                  change(s) retained.
                </p>
              )}
            </section>
          )}
          <div className="mt-4 space-y-2" data-testid="meeting-agenda-list">
            {selectedMeeting.agenda_items?.map((item) => (
              <div
                className="border border-slate-200 bg-white p-3 text-sm"
                key={item.agenda_id}
              >
                <strong>
                  {item.sequence}. {item.title}
                </strong>
                {item.notes && (
                  <p className="mt-1 text-slate-600">{item.notes}</p>
                )}
              </div>
            ))}
          </div>

          {canRecordMeeting && (
            <div className="mt-6 grid gap-6 xl:grid-cols-2">
              <section
                className="border border-slate-200 bg-white p-5"
                data-testid="meeting-attendance-panel"
              >
                <h3 className="font-display text-lg font-semibold">
                  Attendance & quorum
                </h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <select
                    className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                    data-testid="meeting-attendance-person"
                    onChange={(event) =>
                      setAttendance({
                        ...attendance,
                        person_id: event.target.value,
                      })
                    }
                    value={attendance.person_id}
                  >
                    <option value="">Select resident</option>
                    {bootstrap.people.map((person) => (
                      <option key={person.person_id} value={person.person_id}>
                        {person.display_name}
                      </option>
                    ))}
                  </select>
                  <select
                    className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                    data-testid="meeting-attendance-status"
                    onChange={(event) =>
                      setAttendance({
                        ...attendance,
                        status: event.target.value,
                      })
                    }
                    value={attendance.status}
                  >
                    <option value="PRESENT">Present</option>
                    <option value="ABSENT">Absent</option>
                    <option value="EXCUSED">Excused</option>
                  </select>
                </div>
                <Button
                  className="mt-3"
                  data-testid="meeting-record-attendance"
                  onClick={() =>
                    run(
                      () =>
                        api.post(
                          `/society-meetings/meetings/${selectedMeeting.meeting_id}/attendance`,
                          attendance,
                        ),
                      "Attendance recorded",
                    )
                  }
                  size="sm"
                  variant="outline"
                >
                  <ClipboardCheck className="h-4 w-4" />
                  Record attendance
                </Button>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <Input
                    data-testid="meeting-quorum-eligible"
                    min="0"
                    onChange={(event) =>
                      setQuorum({
                        ...quorum,
                        eligible_count: event.target.value,
                      })
                    }
                    placeholder="Eligible count"
                    type="number"
                    value={quorum.eligible_count}
                  />
                  <Input
                    data-testid="meeting-quorum-required"
                    min="0"
                    onChange={(event) =>
                      setQuorum({
                        ...quorum,
                        required_count: event.target.value,
                      })
                    }
                    placeholder="Recorded requirement"
                    type="number"
                    value={quorum.required_count}
                  />
                  <Textarea
                    className="sm:col-span-2"
                    data-testid="meeting-quorum-notes"
                    onChange={(event) =>
                      setQuorum({ ...quorum, notes: event.target.value })
                    }
                    placeholder="Administrative record only; no legal determination is made."
                    rows={2}
                    value={quorum.notes}
                  />
                </div>
                <Button
                  className="mt-3"
                  data-testid="meeting-record-quorum"
                  onClick={() =>
                    run(
                      () =>
                        api.post(
                          `/society-meetings/meetings/${selectedMeeting.meeting_id}/quorum`,
                          quorum,
                        ),
                      "Quorum recorded",
                    )
                  }
                  size="sm"
                >
                  Record quorum
                </Button>
              </section>

              <section
                className="border border-slate-200 bg-white p-5"
                data-testid="meeting-minutes-panel"
              >
                <h3 className="font-display text-lg font-semibold">
                  Proceedings & minutes
                </h3>
                <Textarea
                  className="mt-3"
                  data-testid="meeting-minutes-body"
                  disabled={
                    selectedMeeting.minutes_state !== "NONE" &&
                    selectedMeeting.minutes_state !== "DRAFT"
                  }
                  onChange={(event) =>
                    setMinutes({ ...minutes, body: event.target.value })
                  }
                  placeholder="Record proceedings and decisions"
                  rows={6}
                  value={minutes.body}
                />
                <Textarea
                  className="mt-3"
                  data-testid="meeting-minutes-actions"
                  disabled={
                    selectedMeeting.minutes_state !== "NONE" &&
                    selectedMeeting.minutes_state !== "DRAFT"
                  }
                  onChange={(event) =>
                    setMinutes({ ...minutes, action_items: event.target.value })
                  }
                  placeholder="One action item per line"
                  rows={3}
                  value={minutes.action_items}
                />
                <Button
                  className="mt-3"
                  data-testid="meeting-save-minutes"
                  disabled={
                    selectedMeeting.minutes_state === "APPROVED" ||
                    selectedMeeting.minutes_state === "PUBLISHED"
                  }
                  onClick={() =>
                    run(
                      () =>
                        api.post(
                          `/society-meetings/meetings/${selectedMeeting.meeting_id}/minutes`,
                          {
                            body: minutes.body,
                            action_items: minutes.action_items
                              .split("\n")
                              .filter(Boolean),
                          },
                        ),
                      "Minutes saved as draft",
                    )
                  }
                  size="sm"
                  variant="outline"
                >
                  Save minutes draft
                </Button>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button
                    data-testid="meeting-approve-minutes"
                    disabled={selectedMeeting.minutes_state !== "DRAFT"}
                    onClick={() =>
                      run(
                        () =>
                          api.post(
                            `/society-meetings/meetings/${selectedMeeting.meeting_id}` +
                              "/approve-minutes",
                          ),
                        "Minutes approved",
                      )
                    }
                    size="sm"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Approve minutes
                  </Button>
                  <Button
                    data-testid="meeting-publish-minutes"
                    disabled={selectedMeeting.minutes_state !== "APPROVED"}
                    onClick={() =>
                      run(
                        () =>
                          api.post(
                            `/society-meetings/meetings/${selectedMeeting.meeting_id}` +
                              "/publish-minutes",
                          ),
                        "Published minutes are now resident-safe",
                      )
                    }
                    size="sm"
                    variant="outline"
                  >
                    Publish minutes
                  </Button>
                </div>
              </section>
            </div>
          )}

          {canRecordMeeting && (
            <section
              className="mt-6 border border-slate-200 bg-white p-5"
              data-testid="meeting-resolutions-panel"
            >
              <h3 className="font-display text-lg font-semibold">
                Resolutions
              </h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Input
                  data-testid="meeting-resolution-title"
                  disabled={
                    selectedMeeting.minutes_state === "APPROVED" ||
                    selectedMeeting.minutes_state === "PUBLISHED"
                  }
                  onChange={(event) =>
                    setResolution({ ...resolution, title: event.target.value })
                  }
                  placeholder="Resolution title"
                  value={resolution.title}
                />
                <Input
                  data-testid="meeting-resolution-outcome"
                  disabled={
                    selectedMeeting.minutes_state === "APPROVED" ||
                    selectedMeeting.minutes_state === "PUBLISHED"
                  }
                  onChange={(event) =>
                    setResolution({
                      ...resolution,
                      outcome: event.target.value,
                    })
                  }
                  placeholder="Recorded outcome"
                  value={resolution.outcome}
                />
                <Textarea
                  className="sm:col-span-2"
                  data-testid="meeting-resolution-text"
                  disabled={
                    selectedMeeting.minutes_state === "APPROVED" ||
                    selectedMeeting.minutes_state === "PUBLISHED"
                  }
                  onChange={(event) =>
                    setResolution({ ...resolution, text: event.target.value })
                  }
                  placeholder="Resolution text"
                  rows={3}
                  value={resolution.text}
                />
                <Input
                  data-testid="meeting-resolution-for"
                  min="0"
                  onChange={(event) =>
                    setResolution({
                      ...resolution,
                      recorded_for: event.target.value,
                    })
                  }
                  placeholder="Recorded for"
                  type="number"
                  value={resolution.recorded_for}
                />
                <Input
                  data-testid="meeting-resolution-against"
                  min="0"
                  onChange={(event) =>
                    setResolution({
                      ...resolution,
                      recorded_against: event.target.value,
                    })
                  }
                  placeholder="Recorded against"
                  type="number"
                  value={resolution.recorded_against}
                />
                <Input
                  data-testid="meeting-resolution-abstain"
                  min="0"
                  onChange={(event) =>
                    setResolution({
                      ...resolution,
                      recorded_abstain: event.target.value,
                    })
                  }
                  placeholder="Recorded abstain"
                  type="number"
                  value={resolution.recorded_abstain}
                />
              </div>
              <Button
                className="mt-3"
                data-testid="meeting-add-resolution"
                disabled={
                  selectedMeeting.minutes_state === "APPROVED" ||
                  selectedMeeting.minutes_state === "PUBLISHED"
                }
                onClick={async () => {
                  const result = await run(
                    () =>
                      api.post(
                        `/society-meetings/meetings/${selectedMeeting.meeting_id}/resolutions`,
                        resolution,
                      ),
                    "Resolution added",
                  );
                  if (result) {
                    setResolution({
                      title: "",
                      text: "",
                      outcome: "",
                      recorded_for: "",
                      recorded_against: "",
                      recorded_abstain: "",
                    });
                  }
                }}
                size="sm"
                variant="outline"
              >
                <Plus className="h-4 w-4" />
                Add resolution
              </Button>
              <div
                className="mt-4 space-y-2"
                data-testid="meeting-resolution-list"
              >
                {(detail?.resolutions || []).map((item) => (
                  <article
                    className="border border-slate-200 p-3 text-sm"
                    key={item.resolution_id}
                  >
                    <strong>
                      {item.sequence}. {item.title}
                    </strong>
                    <p className="mt-1 whitespace-pre-wrap text-slate-700">
                      {item.text}
                    </p>
                    {item.outcome && (
                      <p className="mt-2 text-xs font-semibold text-blue-700">
                        {item.outcome}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-slate-500">
                      Administrative record: {item.recorded_for || 0} for ·{" "}
                      {item.recorded_against || 0} against ·{" "}
                      {item.recorded_abstain || 0} abstain
                    </p>
                  </article>
                ))}
              </div>
            </section>
          )}
        </section>
      )}
    </div>
  );
}
